import { useEffect, useState } from 'react';
import { interviewRuntimeAPI } from './api/interviewRuntimeAPI';
import { isUpgradeRequiredError } from './components/InterviewUpgradeGate';
import { REPORT_PREFIX } from './interviewProgressStorage';

/**
 * The marked answers behind the Feedback and Review pages, for one finished
 * written paper or live interview (its run ids). Both pages read the same
 * POST /sittings/feedback; only Feedback asks for the written report, which
 * is one model call and takes about fifteen seconds. Kept for the tab's
 * lifetime so moving between the two pages doesn't fetch or write it again.
 */
const PREFIX = REPORT_PREFIX;

function readCache(idKey) {
  try {
    const raw = sessionStorage.getItem(PREFIX + idKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(idKey, value) {
  try {
    sessionStorage.setItem(PREFIX + idKey, JSON.stringify(value));
  } catch { /* the page still works, it just fetches again */ }
}

/**
 * status: 'no-runs' | 'loading' | 'ready' | 'error' | 'upgrade'
 * reportStatus (withReport only): 'loading' | 'ready' | 'unavailable'
 */
export function useSittingReport(runIds, { withReport = false, mode = 'live' } = {}) {
  const idKey = (runIds || []).join(',');
  const [state, setState] = useState(() => ({
    status: idKey ? 'loading' : 'no-runs', sitting: [], narrative: null, reportStatus: 'loading',
  }));

  useEffect(() => {
    if (!idKey) {
      setState({ status: 'no-runs', sitting: [], narrative: null, reportStatus: 'unavailable' });
      return undefined;
    }
    let cancelled = false;
    const cached = readCache(idKey);
    const reportDone = cached?.withReport;

    if (cached) {
      setState({
        status: 'ready',
        sitting: cached.sitting || [],
        narrative: cached.narrative || null,
        reportStatus: reportDone ? (cached.narrative ? 'ready' : 'unavailable') : 'loading',
      });
      if (!withReport || reportDone) return undefined;
    } else {
      setState((s) => ({ ...s, status: 'loading', reportStatus: 'loading' }));
    }

    const marksFailed = (err) => {
      if (cancelled) return;
      setState((s) => (s.status === 'ready'
        ? s
        : { ...s, status: isUpgradeRequiredError(err) ? 'upgrade' : 'error' }));
    };
    const reportFailed = () => {
      if (!cancelled) setState((s) => ({ ...s, reportStatus: 'unavailable' }));
    };

    // The marks come back quickly; the report takes a model call. Ask for the
    // marks on their own so they show at once, and the report alongside.
    if (!cached) {
      interviewRuntimeAPI.getSittingFeedback(runIds, { includeReport: false, mode })
        .then((data) => {
          if (cancelled) return;
          if (!readCache(idKey)) writeCache(idKey, { sitting: data.sitting || [], narrative: null, withReport: false });
          setState((s) => (s.status === 'ready' ? s : {
            ...s, status: 'ready', sitting: data.sitting || [],
          }));
        })
        .catch(marksFailed);
    }
    if (withReport) {
      interviewRuntimeAPI.getSittingFeedback(runIds, { includeReport: true, mode })
        .then((data) => {
          if (cancelled) return;
          writeCache(idKey, { sitting: data.sitting || [], narrative: data.narrative || null, withReport: true });
          setState({
            status: 'ready',
            sitting: data.sitting || [],
            narrative: data.narrative || null,
            reportStatus: data.narrative ? 'ready' : 'unavailable',
          });
        })
        .catch(reportFailed);
    }
    return () => { cancelled = true; };
    // runIds is keyed by idKey; mode never changes for one set of runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey, withReport]);

  return state;
}

/** Each follow-up's answers, from the conversation: the candidate's turns
 * recorded against it, in order (a live interview can take more than one
 * turn on a follow-up). A question with no follow-ups has one entry whose
 * answers are the turns recorded against no follow-up.
 *
 * Each answer carries the examiner's line it replied to (`asked`). A second
 * answer on one follow-up replies to a hint, a mistake follow-up or a harder
 * follow-up, which the page shows above it; without it, "No idea." sat under
 * the follow-up's own question with nothing saying what it answered. */
export function answersByFollowUp(question) {
  let lastAsked = null;
  const turns = [];
  (question.transcript || []).forEach((t) => {
    if (t.speaker === 'interviewer') lastAsked = t.content;
    else if (t.speaker === 'candidate') turns.push({ ...t, asked: lastAsked });
  });
  return (question.rungs || []).map((rung) => {
    const mine = turns
      .filter((t) => (rung.opening_only ? !t.rung_id : t.rung_id === rung.rung_id))
      .filter((t) => t.content && t.content !== '(no answer)');
    return {
      ...rung,
      answers: mine.map((t) => t.content),
      replies: mine.map((t, i) => ({ text: t.content, asked: i > 0 ? t.asked : null })),
    };
  });
}
